import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import {
  getAnomalyDefaultPeriodFrom,
  getAnomalyDefaultPeriodTo,
  resolveAnomalyPeriodState,
} from "../anomalyDateUtils";
import { useAnomalyDetection } from "../AnomalyDetectionContext";
import CompanyService from "../../../../core/service/CompanyService";
import useAnomalyTimeSeries from "./hooks/useAnomalyTimeSeries";
import { sliceDataPoints, buildRange } from "./utils/timelineRange";
import {
  hasMissingValueAnomalies,
  hasOnlyMissingValueAnomalies,
  deriveTimeSeriesSummaryFromDataPoints,
} from "./utils/timelineHealth";
import FilterBar from "./components/FilterBar";
import RuleLensTabs from "./components/RuleLensTabs";
import SummaryStrip from "./components/SummaryStrip";
import ChartViewTabs from "./components/ChartViewTabs";
import DeviationChart from "./components/DeviationChart";
import PeriodHeatmap from "./components/PeriodHeatmap";
import SelectedPeriodPanel from "./components/SelectedPeriodPanel";
import PatternPanel from "./components/PatternPanel";
import TimelineNarrative from "./components/TimelineNarrative";
import HealthyUnhealthyComparison from "./components/HealthyUnhealthyComparison";
import TimelineSkeleton from "./components/TimelineSkeleton";
import TimelineEmptyState from "./components/TimelineEmptyState";
import TimelineErrorState from "./components/TimelineErrorState";
import { formatTimelinePeriod } from "./utils/timelineFormatters";
import "./MetricAnomalyTimeline.scss";

const MetricAnomalyTimeline = ({ t }) => {
  const { lastCompletedAt } = useAnomalyDetection();
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state || {};

  const initialPeriods = resolveAnomalyPeriodState(
    navState.periodFrom,
    navState.periodTo,
    getAnomalyDefaultPeriodFrom(),
    getAnomalyDefaultPeriodTo()
  );

  const [metricId] = useState(navState.metricId ? String(navState.metricId) : "");
  const [metricName] = useState(navState.metricName || "");
  const [branchId, setBranchId] = useState(navState.branchId ? String(navState.branchId) : "");
  const [branchName, setBranchName] = useState(navState.branchName || "");
  const [datapointId, setDatapointId] = useState(navState.datapointId ? String(navState.datapointId) : "");
  const [datapointName] = useState(navState.datapointName || "");
  const [childBranches, setChildBranches] = useState([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const [periodFrom, setPeriodFrom] = useState(initialPeriods.periodFrom);
  const [periodTo, setPeriodTo] = useState(initialPeriods.periodTo);
  const [status, setStatus] = useState(navState.status || "");
  const [severity, setSeverity] = useState(navState.severity || "");
  const [ruleCode, setRuleCode] = useState(navState.ruleCode || "");

  const [selectedPeriod, setSelectedPeriod] = useState(navState.selectedPeriod || null);
  const [visibleRange, setVisibleRange] = useState(null);
  const [rangeAnchorPeriod, setRangeAnchorPeriod] = useState(null);

  const filters = useMemo(
    () => ({
      metricId,
      branchId,
      datapointId,
      status,
      severity,
      ruleCode,
      periodFrom,
      periodTo,
    }),
    [metricId, branchId, datapointId, status, severity, ruleCode, periodFrom, periodTo]
  );

  const {
    summary,
    dataPoints,
    isLoading,
    error,
    retry,
    hasLoaded,
  } = useAnomalyTimeSeries(filters, t, lastCompletedAt);

  const visibleDataPoints = useMemo(
    () => sliceDataPoints(dataPoints, visibleRange),
    [dataPoints, visibleRange]
  );

  const displaySummary = useMemo(() => {
    if (visibleRange) {
      return deriveTimeSeriesSummaryFromDataPoints(visibleDataPoints);
    }
    return summary;
  }, [visibleRange, visibleDataPoints, summary]);

  const selectedDataPoint = useMemo(
    () => dataPoints.find((dp) => dp.period === selectedPeriod) || null,
    [dataPoints, selectedPeriod]
  );

  useEffect(() => {
    if (!hasLoaded || !dataPoints.length) return;
    const viewPoints = visibleRange ? visibleDataPoints : dataPoints;
    if (!viewPoints.length) return;

    const selectionValid = selectedPeriod
      && viewPoints.some((dp) => dp.period === selectedPeriod);
    if (selectionValid) return;

    const defaultPeriod = viewPoints[0].period;
    setSelectedPeriod(defaultPeriod);
    setRangeAnchorPeriod(defaultPeriod);
  }, [hasLoaded, dataPoints, visibleDataPoints, visibleRange, selectedPeriod]);

  const handlePeriodSelect = useCallback((period, isShiftClick) => {
    if (isShiftClick) {
      setRangeAnchorPeriod(period);
      setSelectedPeriod(period);
    } else {
      setRangeAnchorPeriod(period);
      setSelectedPeriod(period);
      setVisibleRange(null);
    }
  }, []);

  const handleRangeSelect = useCallback(
    (endPeriod) => {
      if (!rangeAnchorPeriod) return;
      setVisibleRange(buildRange(rangeAnchorPeriod, endPeriod));
      setSelectedPeriod(endPeriod);
    },
    [rangeAnchorPeriod]
  );

  const clearVisibleRange = () => {
    setVisibleRange(null);
    setRangeAnchorPeriod(null);
  };

  useEffect(() => {
    setIsLoadingBranches(true);
    CompanyService.getEnterprisesByLevel(2)
      .then((res) => {
        if (res.status !== 200) return;
        const sorted = [...(res.data || [])].sort((a, b) => {
          const parentOrder = (a.parent?.sortOrder ?? 0) - (b.parent?.sortOrder ?? 0);
          if (parentOrder !== 0) return parentOrder;
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        });
        setChildBranches(sorted);
      })
      .catch(() => setChildBranches([]))
      .finally(() => setIsLoadingBranches(false));
  }, []);

  useEffect(() => {
    if (!branchId || branchName || childBranches.length === 0) return;
    const match = childBranches.find((u) => String(u.id) === String(branchId));
    if (match) setBranchName(match.name);
  }, [branchId, branchName, childBranches]);

  const handleBranchChange = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setBranchId("");
      setBranchName("");
      return;
    }
    const match = childBranches.find((u) => String(u.id) === selectedId);
    setBranchId(selectedId);
    setBranchName(match?.name || "");
  };

  const totalAnomalies = displaySummary?.totalAnomalies ?? 0;
  const showMissingBanner = hasMissingValueAnomalies(visibleDataPoints);
  const showMissingOnlyBanner = hasOnlyMissingValueAnomalies(visibleDataPoints);

  return (
    <div className="metric-anomaly-timeline" data-testid="metric-anomaly-timeline">
      <div className="List_header mb-3">
        <h5 className="MH">
          {navState && Object.keys(navState).length > 0 && (
            <span
              className="back-nav-btn anomaly-back-nav-btn"
              onClick={() => navigate(-1)}
              title={t("Back.label")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate(-1)}
            >
              &#8592;
            </span>
          )}
          {t("MetricAnomalyTimeline.pageTitle.label")}
        </h5>
      </div>

      <FilterBar
        metricId={metricId}
        metricName={metricName}
        branchId={branchId}
        branchName={branchName}
        datapointId={datapointId}
        datapointName={datapointName}
        childBranches={childBranches}
        isLoadingBranches={isLoadingBranches}
        status={status}
        severity={severity}
        ruleCode={ruleCode}
        onPeriodChange={(from, to) => { setPeriodFrom(from); setPeriodTo(to); }}
        onBranchChange={handleBranchChange}
        onStatusChange={setStatus}
        onSeverityChange={setSeverity}
        onRuleCodeChange={setRuleCode}
        onClearBranch={() => { setBranchId(""); setBranchName(""); }}
        onClearDatapoint={() => setDatapointId("")}
      />

      <RuleLensTabs ruleCode={ruleCode} onRuleCodeChange={setRuleCode} />

      {!metricId ? (
        <div className="anomaly-chart-card">
          <div className="chart-card-body">
            <TimelineEmptyState variant="no-metric" />
          </div>
        </div>
      ) : error ? (
        <div className="anomaly-chart-card">
          <div className="chart-card-body">
            <TimelineErrorState onRetry={retry} />
          </div>
        </div>
      ) : isLoading && !hasLoaded ? (
        <TimelineSkeleton />
      ) : hasLoaded && dataPoints.length === 0 ? (
        <div className="anomaly-chart-card">
          <div className="chart-card-body">
            <TimelineEmptyState variant="no-data" />
          </div>
        </div>
      ) : (
        <>
          {isLoading && <div className="mat-loading-overlay">{t("MetricAnomalyTimeline.refreshing.label")}</div>}

          <SummaryStrip summary={displaySummary} dataPoints={visibleDataPoints} />

          {visibleRange && (
            <div className="mat-range-chip">
              <span>
                {t("MetricAnomalyTimeline.rangeZoom.label", {
                  from: formatTimelinePeriod(visibleRange.from),
                  to: formatTimelinePeriod(visibleRange.to),
                })}
              </span>
              <Button variant="link" size="sm" className="mat-range-clear" onClick={clearVisibleRange}>
                {t("MetricAnomalyTimeline.resetZoom.label")}
              </Button>
            </div>
          )}

          {totalAnomalies === 0 && (
            <div className="mat-banner mat-banner-info">
              {t("MetricAnomalyTimeline.noAnomaliesInRange.label")}
              <small>{t("AnomalyDetection.widenDateRange.label")}</small>
            </div>
          )}

          {showMissingBanner && (
            <div className="mat-banner mat-banner-warning">
              {showMissingOnlyBanner
                ? t("MetricAnomalyTimeline.missingValueOnlyBanner.label")
                : t("MetricAnomalyTimeline.missingValueBanner.label")}
            </div>
          )}

          <ChartViewTabs
            dataPoints={visibleDataPoints}
            ruleCode={ruleCode}
            selectedPeriod={selectedPeriod}
            onPeriodSelect={handlePeriodSelect}
            onRangeSelect={handleRangeSelect}
            rangeAnchorPeriod={rangeAnchorPeriod}
          />

          <DeviationChart dataPoints={visibleDataPoints} />

          <PeriodHeatmap
            dataPoints={visibleDataPoints}
            selectedPeriod={selectedPeriod}
            visibleRange={visibleRange}
            onPeriodSelect={handlePeriodSelect}
            onRangeSelect={handleRangeSelect}
            rangeAnchorPeriod={rangeAnchorPeriod}
          />

          <div className="row g-3 mat-panels-row">
            <div className="col-lg-5">
              <SelectedPeriodPanel
                dataPoint={selectedDataPoint}
                statusFilter={status}
                ruleCode={ruleCode}
              />
            </div>
            <div className="col-lg-7">
              <PatternPanel dataPoints={visibleDataPoints} />
            </div>
          </div>

          <TimelineNarrative
            dataPoints={visibleDataPoints}
            selectedPeriod={selectedPeriod}
            onPeriodSelect={handlePeriodSelect}
          />

          <HealthyUnhealthyComparison dataPoints={visibleDataPoints} />
        </>
      )}
    </div>
  );
};

export default withTranslation()(MetricAnomalyTimeline);
